import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ExternalLink,
  RotateCcw,
  Square,
  Tag,
  Undo2,
  Wand2,
} from 'lucide-react';
import {
  roundToNearest5,
  type JobItem,
  type PricingBatch,
  type PricingCandidate,
  type PricingResultV2,
} from '@portal/shared';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Switch,
  useToast,
} from '../../components/index.js';
import { api } from '../../lib/api-client.js';
import { track } from '../../lib/analytics/index.js';

const POLL_MS = 2500;

// Engine stages that mean a job is finished — no more work, no more polling.
const TERMINAL_STAGES = new Set(['done', 'error', 'cancelled']);

// Confidence ordering for the sortable "Confidence" column and the default
// "needs-review first" sort. This is sort logic, not an enum->text map — the
// engine still owns all display text via the `*_label` fields (decision D23).
const CONFIDENCE_RANK: Record<string, number> = {
  no_price: 0,
  estimate_weak: 1,
  estimate_from_comparables: 2,
  same_product: 3,
  exact_model: 4,
  historical: 5,
};

function fmtCAD(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** Format a duration in seconds as "Hh Mm Ss", dropping zero leading units. */
function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (h > 0 || m > 0) parts.push(`${m}m`);
  parts.push(`${sec}s`);
  return parts.join(' ');
}

/** Effective value for a line item: the accepted going-concern price, else
 *  Greg's dictated value. Treats $0 as "no price" — recordings without a
 *  dictated price land with value 0 and counting those as priced is misleading.
 *
 *  Auto-accept (server side) already wrote `acceptedPrice` for every item the
 *  engine was confident about (needs_review === false); items the engine
 *  flagged for review keep Greg's value until he accepts. */
function effectivePrice(it: JobItem): number | null {
  const v = it.acceptedPrice ?? it.greg_value ?? null;
  return v != null && v > 0 ? v : null;
}

// ---------------------------------------------------------------------------
// Live engine shapes — the v2 polling payload (stt-service PricingManagerV2).
// ---------------------------------------------------------------------------

/** One per-item summary from GET /v2/pricing/batch/{id}. The engine emits
 *  `*_label` display strings so the UI never hardcodes an enum->text map. */
interface LiveJobSummary {
  job_id: string;
  client_id?: string | null;
  description: string;
  greg_value?: number | null;
  stage: string;
  stage_label?: string;
  is_done?: boolean;
  is_cancelled?: boolean;
  confidence: string;
  confidence_label?: string;
  needs_review?: boolean;
  review_reason?: string | null;
  pipeline_path?: string | null;
  pipeline_path_label?: string | null;
  valuation?: { rv?: number | null; gc?: number | null; olv?: number | null; flv?: number | null };
  greg_value_check?: {
    greg_value?: number | null;
    engine_rv?: number | null;
    ratio?: number | null;
    verdict?: string;
  };
  retrieval_health?: {
    sources_attempted?: number;
    sources_ok?: number;
    sources_failed?: { supplier: string; domain: string; reason: string }[];
    degraded?: boolean;
  };
  match_reason?: string | null;
  elapsed_s?: number | null;
  error?: { kind: string; message: string } | null;
}

interface LiveBatchStatus {
  total: number;
  completed: number;
  in_flight: number;
  progress: number;
  elapsed_s?: number;
  is_done: boolean;
  is_cancelled: boolean;
  items: LiveJobSummary[];
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function ValuationCell({
  label,
  hint,
  value,
  emphasize,
}: {
  label: string;
  hint: string;
  value: number | null | undefined;
  emphasize?: boolean;
}) {
  return (
    <span title={hint} className="inline-flex items-baseline gap-1">
      <span className="uppercase tracking-wide text-text-tertiary">{label}</span>
      <span className={'tabular-nums ' + (emphasize ? 'text-text-primary font-semibold' : 'text-text-primary')}>
        {fmtCAD(value)}
      </span>
    </span>
  );
}

function Stat({
  label,
  value,
  sub,
  variant = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  variant?: 'neutral' | 'success' | 'warning';
}) {
  const valueColor =
    variant === 'success' ? 'text-success' : variant === 'warning' ? 'text-warning' : 'text-text-primary';
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${valueColor}`}>{value}</div>
      {sub && <div className="text-xs text-text-tertiary">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row snapshot — unifies the live poll tick with the persisted decision blob.
// ---------------------------------------------------------------------------

interface RowSnapshot {
  item: JobItem;
  live: LiveJobSummary | undefined;
  decision: PricingResultV2 | null;
  /** Engine job id — needed to cancel / re-run a single item. */
  engineJobId: string | null;
  stage: string | null;
  inFlight: boolean;
  /** GC value shown in the row: accepted price, else Greg's dictated value. */
  effective: number | null;
  /** Engine's suggested going-concern value (valuation.gc). */
  suggestedGc: number | null;
  confidence: string | null;
  confidenceLabel: string | null;
  pipelinePathLabel: string | null;
  stageLabel: string | null;
  needsReview: boolean;
  reviewReason: string | null;
  degraded: boolean;
  isWalkIn: boolean;
  error: { kind: string; message: string } | null;
}

function buildSnapshot(item: JobItem, live: LiveJobSummary | undefined): RowSnapshot {
  const decision = (item.pricingDecision as PricingResultV2 | null | undefined) ?? null;
  const stage =
    live?.stage ??
    decision?.stage ??
    (item.acceptedPrice != null || item.suggestedGc != null ? 'done' : null);
  const inFlight = stage != null && !TERMINAL_STAGES.has(stage);

  const valuation = live?.valuation ?? decision?.valuation;
  const health = live?.retrieval_health ?? decision?.retrieval_health;

  return {
    item,
    live,
    decision,
    engineJobId: live?.job_id ?? decision?.job_id ?? null,
    stage,
    inFlight,
    effective: effectivePrice(item),
    suggestedGc: valuation?.gc ?? item.suggestedGc ?? null,
    confidence: live?.confidence ?? decision?.confidence ?? item.suggestionConfidence ?? null,
    confidenceLabel: live?.confidence_label ?? decision?.confidence_label ?? null,
    pipelinePathLabel: live?.pipeline_path_label ?? decision?.pipeline_path_label ?? null,
    stageLabel: live?.stage_label ?? decision?.stage_label ?? null,
    needsReview: live?.needs_review ?? decision?.needs_review ?? item.needsReview ?? false,
    reviewReason: live?.review_reason ?? decision?.review_reason ?? null,
    degraded: health?.degraded ?? false,
    isWalkIn: decision?.walk_in?.is_walk_in ?? false,
    error: live?.error ?? decision?.error ?? null,
  };
}

/** Badge colour for the confidence cell. Derived from review state, not a
 *  hardcoded confidence->colour map — the label text comes from the engine. */
function confidenceVariant(snap: RowSnapshot): 'success' | 'warning' | 'danger' | 'neutral' {
  if (snap.error) return 'danger';
  if (snap.confidence == null) return 'neutral';
  if (snap.confidence === 'no_price') return 'neutral';
  return snap.needsReview ? 'warning' : 'success';
}

// ---------------------------------------------------------------------------
// Optimistic override reconciliation
// ---------------------------------------------------------------------------

export type PendingOverride = { acceptedPrice: number | null; priceOverridden: boolean };

/** Reconcile the optimistic override layer against a fresh `itemsProp`.
 *
 *  Overrides committed by `setPrice` are persisted server-side immediately but
 *  only live client-side in `pendingOverrides` until a real server refresh
 *  (GET /items) brings the persisted values back through `itemsProp`. We must
 *  NOT blanket-clear them on every `itemsProp` change: an unrelated edit
 *  elsewhere on the page — e.g. correcting a line-item description in the
 *  Dictation card — produces a brand-new `items` array (via `onItemChange`)
 *  that still carries the pre-override `acceptedPrice`. A blanket clear then
 *  makes every override Greg just set visually revert, even though they are
 *  safe in Firestore. (P0 — see Portal_Bug_P0_pricing-override-wipe.md.)
 *
 *  Instead, keep each optimistic entry until `itemsProp` actually reflects it
 *  (server truth has caught up) or the item disappears. Returns `prev`
 *  unchanged when nothing is dropped, so the effect stays referentially stable
 *  and doesn't churn renders. */
export function reconcileOverrides(
  prev: Record<string, PendingOverride>,
  itemsProp: JobItem[],
): Record<string, PendingOverride> {
  const ids = Object.keys(prev);
  if (ids.length === 0) return prev;
  const byId = new Map(itemsProp.map((it) => [it.id, it]));
  const next: Record<string, PendingOverride> = {};
  for (const id of ids) {
    const ov = prev[id]!;
    const server = byId.get(id);
    if (!server) continue; // item gone — drop the stale optimistic copy
    const serverMatches =
      (server.acceptedPrice ?? null) === ov.acceptedPrice &&
      !!server.priceOverridden === ov.priceOverridden;
    if (serverMatches) continue; // server caught up — let itemsProp own it
    next[id] = ov; // server still stale — keep the optimistic value
  }
  // Referential stability: every key survived ⇒ contents are identical to prev.
  return Object.keys(next).length === ids.length ? prev : next;
}

// ---------------------------------------------------------------------------
// PricingCard — the card
// ---------------------------------------------------------------------------

export interface PricingCardProps {
  jobId: string;
  items: JobItem[];
  batches: PricingBatch[];
  canEdit: boolean;
  /** Items-only refresh. */
  onChange: () => void | Promise<void>;
  /** Full job-doc refresh — picks up the new PricingBatch after a batch start. */
  onBatchStart: () => void | Promise<void>;
  /** Patch local items state from a fresh server snapshot (poll / re-run). */
  onItemsPatch: (items: JobItem[]) => void;
}

export function PricingCard({
  jobId,
  items: itemsProp,
  batches,
  canEdit,
  onChange,
  onBatchStart,
  onItemsPatch,
}: PricingCardProps) {
  const { toast } = useToast();

  // Optimistic local overrides — applied on top of itemsProp until the parent
  // refresh syncs server truth back.
  const [pendingOverrides, setPendingOverrides] = useState<
    Record<string, PendingOverride>
  >({});

  // Don't wipe optimistic overrides wholesale on every itemsProp change — an
  // unrelated edit (e.g. a description fix in the Dictation card) rebuilds the
  // items array and would otherwise revert every override Greg just set.
  // Reconcile instead: keep each override until server truth reflects it.
  useEffect(() => {
    setPendingOverrides((prev) => reconcileOverrides(prev, itemsProp));
  }, [itemsProp]);

  const items = useMemo(() => {
    if (Object.keys(pendingOverrides).length === 0) return itemsProp;
    return itemsProp.map((it) =>
      pendingOverrides[it.id]
        ? {
            ...it,
            acceptedPrice: pendingOverrides[it.id]!.acceptedPrice,
            priceOverridden: pendingOverrides[it.id]!.priceOverridden,
            acceptedAt: new Date().toISOString(),
          }
        : it,
    );
  }, [itemsProp, pendingOverrides]);

  const applyLocalOverride = useCallback(
    (itemId: string, acceptedPrice: number | null, priceOverridden: boolean) => {
      setPendingOverrides((prev) => ({
        ...prev,
        [itemId]: { acceptedPrice, priceOverridden },
      }));
    },
    [],
  );

  const priceable = useMemo(
    () => items.filter((it) => !it.isHeader && !!it.description),
    [items],
  );
  const unpricedIds = useMemo(
    () =>
      priceable
        .filter((it) => it.acceptedPrice == null && (it.greg_value == null || it.greg_value === 0))
        .map((it) => it.id),
    [priceable],
  );
  const itemsWithAnyPrice = useMemo(
    () => priceable.filter((it) => effectivePrice(it) != null),
    [priceable],
  );

  const latestBatch = useMemo(() => {
    if (batches.length === 0) return null;
    return [...batches].sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]!;
  }, [batches]);

  // Live engine state — owned here so polling doesn't churn the parent.
  const [live, setLive] = useState<Record<string, LiveJobSummary>>({});
  const [batchStatus, setBatchStatus] = useState<LiveBatchStatus | null>(null);
  const [finishedAt, setFinishedAt] = useState<number | null>(null);
  useEffect(() => {
    if (finishedAt == null) return;
    const t = setTimeout(() => setFinishedAt(null), 5000);
    return () => clearTimeout(t);
  }, [finishedAt]);

  const [finishedBatchIds, setFinishedBatchIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setFinishedBatchIds((prev) => {
      const valid = new Set(batches.map((b) => b.batchId));
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [batches]);
  const runningBatchIds = useMemo(
    () =>
      batches
        .filter((b) => b.status === 'running' && !finishedBatchIds.has(b.batchId))
        .map((b) => b.batchId),
    [batches, finishedBatchIds],
  );
  const runningKey = runningBatchIds.join(',');
  const batchRunning = runningBatchIds.length > 0;
  const activeBatchId = batchRunning ? latestBatch?.batchId ?? null : null;

  useEffect(() => {
    if (runningBatchIds.length === 0) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (const bid of runningBatchIds) {
      const tick = async () => {
        try {
          const res = await api.get<{
            batch: PricingBatch;
            engine: LiveBatchStatus;
            items?: JobItem[];
          }>(`/api/jobs/${jobId}/pricing-batches/${bid}`);
          if (cancelled) return;
          setBatchStatus(res.engine);
          setLive((prev) => {
            const next = { ...prev };
            for (const sum of res.engine.items) {
              if (sum.client_id) next[sum.client_id] = sum;
            }
            return next;
          });
          if (res.items) onItemsPatch(res.items);

          if (res.engine.is_done || res.engine.is_cancelled) {
            if (res.engine.is_done && !res.engine.is_cancelled) {
              setFinishedAt(Date.now());
            }
            setFinishedBatchIds((prev) => {
              if (prev.has(bid)) return prev;
              const next = new Set(prev);
              next.add(bid);
              return next;
            });
            return;
          }
        } catch {
          /* swallow — retry next tick */
        }
        if (!cancelled) {
          const t = setTimeout(() => void tick(), POLL_MS);
          timers.push(t);
        }
      };
      void tick();
    }

    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, runningKey]);

  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rerunningDegraded, setRerunningDegraded] = useState(false);
  const [forceAccepting, setForceAccepting] = useState(false);
  const [showPrePriced, setShowPrePriced] = useState(false);

  const prePricedCount = useMemo(
    () =>
      priceable.filter(
        (it) => it.greg_value != null && it.greg_value !== 0 && !it.pricingBatchId,
      ).length,
    [priceable],
  );

  // Items whose retrieval pool was degraded by a transient supplier failure.
  const degradedCount = useMemo(
    () =>
      priceable.filter((it) => buildSnapshot(it, live[it.id]).degraded).length,
    [priceable, live],
  );

  const startBatch = useCallback(
    async (itemIds: string[], label: string) => {
      if (itemIds.length === 0) {
        toast({ title: 'No items to price', variant: 'info' });
        return;
      }
      setStarting(true);
      try {
        await api.post(`/api/jobs/${jobId}/pricing-batches`, { itemIds });
        track.interaction('pricing_started_via_ui', {
          jobId,
          targetType: 'job',
          targetId: jobId,
          extra: { itemCount: itemIds.length, label },
        });
        toast({
          title: `Started pricing ${itemIds.length} item${itemIds.length === 1 ? '' : 's'} (${label})`,
          variant: 'success',
        });
        await onBatchStart();
      } catch (err) {
        toast({
          title: 'Failed to start pricing',
          description: err instanceof Error ? err.message : String(err),
          variant: 'danger',
        });
      } finally {
        setStarting(false);
      }
    },
    [jobId, toast, onBatchStart],
  );

  const onPriceAll = useCallback(
    () => startBatch(priceable.map((it) => it.id), 'all'),
    [priceable, startBatch],
  );
  const onPriceUnpriced = useCallback(
    () => startBatch(unpricedIds, 'un-priced only'),
    [unpricedIds, startBatch],
  );

  const onCancel = useCallback(async () => {
    if (!activeBatchId) return;
    setCancelling(true);
    try {
      await api.post(`/api/jobs/${jobId}/pricing-batches/${activeBatchId}/cancel`, {});
      toast({ title: 'Pricing cancelled', variant: 'success' });
      await onChange();
    } catch (err) {
      toast({
        title: 'Cancel failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setCancelling(false);
    }
  }, [jobId, activeBatchId, toast, onChange]);

  // Re-price one item (degraded-pool recovery). Blocks until the engine
  // finishes; the response carries the freshly-ingested items.
  const rerunJob = useCallback(
    async (engineJobId: string) => {
      const res = await api.post<{ ok: boolean; decision: PricingResultV2; items: JobItem[] }>(
        `/api/jobs/${jobId}/pricing-jobs/${engineJobId}/rerun`,
        {},
      );
      if (res.items?.length) onItemsPatch(res.items);
      // Drop any stale live tick for this job so the persisted decision shows.
      setLive((prev) => {
        const cid = res.decision?.client_id;
        if (!cid || !prev[cid]) return prev;
        const next = { ...prev };
        delete next[cid];
        return next;
      });
    },
    [jobId, onItemsPatch],
  );

  // Re-price every degraded item in the latest batch.
  const onRerunDegraded = useCallback(async () => {
    if (!latestBatch) return;
    setRerunningDegraded(true);
    try {
      const res = await api.post<{ ok: boolean; rerun: number; items: JobItem[] }>(
        `/api/jobs/${jobId}/pricing-batches/${latestBatch.batchId}/rerun-degraded`,
        {},
      );
      if (res.items?.length) onItemsPatch(res.items);
      setLive({});
      toast({
        title: `Re-ran ${res.rerun} degraded item${res.rerun === 1 ? '' : 's'}`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: 'Re-run degraded failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setRerunningDegraded(false);
    }
  }, [jobId, latestBatch, onItemsPatch, toast]);

  // Force-accept the engine's suggested GC on every priceable item — even
  // ones flagged needs_review (which normal auto-accept skips per D24).
  // Manual overrides stay sticky; items with no engine result (no_price)
  // are left alone. Debug / bulk-trust action so we can see what the engine
  // would say across the whole spreadsheet without per-row clicks.
  const onForceAcceptEngine = useCallback(async () => {
    if (!window.confirm(
      'Apply the engine’s estimated price to every item, including ones flagged for review? '
      + 'Manual overrides and items with no engine result are left untouched.'
    )) return;
    setForceAccepting(true);
    try {
      const res = await api.post<{
        ok: boolean; accepted: number; skippedOverridden: number; skippedNoSuggestion: number;
      }>(`/api/jobs/${jobId}/items/force-accept-engine`, {});
      track.interaction('force_accept_engine_triggered', {
        jobId,
        targetType: 'job',
        targetId: jobId,
        extra: {
          accepted: res.accepted,
          skippedOverridden: res.skippedOverridden,
          skippedNoSuggestion: res.skippedNoSuggestion,
        },
      });
      toast({
        title: `Applied engine prices to ${res.accepted} item${res.accepted === 1 ? '' : 's'}`,
        description:
          (res.skippedOverridden > 0 ? `${res.skippedOverridden} manual override(s) preserved. ` : '')
          + (res.skippedNoSuggestion > 0 ? `${res.skippedNoSuggestion} item(s) had no engine result.` : ''),
        variant: 'success',
      });
      await onChange();
    } catch (err) {
      toast({
        title: 'Apply engine prices failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setForceAccepting(false);
    }
  }, [jobId, toast, onChange]);

  const onDownloadPricedXlsx = useCallback(async () => {
    setDownloading(true);
    try {
      const token = await (await import('../../lib/firebase.js'))
        .firebase()
        .auth.currentUser?.getIdToken();
      const res = await fetch(`/api/jobs/${jobId}/download.xlsx?stage=pricing`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`download failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pricing.xlsx';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setDownloading(false);
    }
  }, [jobId, toast]);

  const hasAnyPrice = itemsWithAnyPrice.length > 0;
  const hasEngineRun =
    priceable.some((it) => it.pricingBatchId != null) || Object.keys(live).length > 0;

  return (
    <Card>
      <CardHeader
        title="Pricing"
        description={
          priceable.length === 0
            ? 'No priceable items yet — attach a transcription first.'
            : hasAnyPrice
              ? `${itemsWithAnyPrice.length} of ${priceable.length} have a value`
              : `${priceable.length} items, none priced yet`
        }
        actions={
          canEdit && (
            <div className="flex items-center gap-2">
              {hasAnyPrice && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={downloading}
                  onClick={onDownloadPricedXlsx}
                >
                  Download xlsx
                </Button>
              )}
              {batchRunning && (
                <Button
                  variant="danger"
                  size="sm"
                  loading={cancelling}
                  leftIcon={<Square className="size-3.5" aria-hidden />}
                  onClick={onCancel}
                >
                  Stop
                </Button>
              )}
              {!batchRunning && priceable.length > 0 && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<RotateCcw className="size-4" aria-hidden />}
                    loading={starting}
                    onClick={onPriceAll}
                    title="Run the engine on every item and refresh its suggestions. High-confidence suggestions auto-apply; items flagged for review keep Greg's value until accepted (manual overrides are always preserved)."
                  >
                    {hasEngineRun ? 'Refresh suggestions' : 'Get suggestions'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Tag className="size-4" aria-hidden />}
                    loading={starting}
                    disabled={unpricedIds.length === 0}
                    onClick={onPriceUnpriced}
                    title="Skip items with a value from dictation or a prior pricing run"
                  >
                    Price un-priced ({unpricedIds.length})
                  </Button>
                  {hasEngineRun && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<CheckCheck className="size-4" aria-hidden />}
                      loading={forceAccepting}
                      onClick={onForceAcceptEngine}
                      title="Debug: apply the engine's estimated GC to every item, including those flagged for review. Manual overrides and items with no engine result are left untouched."
                    >
                      Apply engine prices to all
                    </Button>
                  )}
                </>
              )}
            </div>
          )
        }
      />
      {(batchRunning || hasEngineRun || hasAnyPrice) && (
        <CardBody className="space-y-3">
          {finishedAt != null && (
            <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success-muted px-3 py-2 text-sm text-success">
              <CheckCircle2 className="size-4 shrink-0" aria-hidden />
              <span>Pricing run finished.</span>
            </div>
          )}
          {!batchRunning && degradedCount > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-warning/30 bg-warning-muted px-3 py-2 text-sm">
              <div className="flex items-center gap-2 text-warning">
                <AlertTriangle className="size-4 shrink-0" aria-hidden />
                <span>
                  {degradedCount} item{degradedCount === 1 ? '' : 's'} priced from a degraded
                  source pool — a supplier was temporarily unavailable.
                </span>
              </div>
              {canEdit && latestBatch && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={rerunningDegraded}
                  leftIcon={<Wand2 className="size-3.5" aria-hidden />}
                  onClick={onRerunDegraded}
                >
                  Re-run degraded
                </Button>
              )}
            </div>
          )}
          <PricingStatsPanel
            items={priceable}
            live={live}
            batchStatus={batchStatus}
            batchRunning={!!batchRunning}
          />
          {prePricedCount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">
                {showPrePriced
                  ? `Showing all ${priceable.length} items`
                  : `${prePricedCount} pre-priced item${prePricedCount === 1 ? '' : 's'} hidden`}
              </span>
              <Switch
                id="show-prepriced"
                label="Include pre-priced"
                checked={showPrePriced}
                onChange={(e) => setShowPrePriced(e.currentTarget.checked)}
              />
            </div>
          )}
          <PriceReviewTable
            jobId={jobId}
            items={priceable}
            live={live}
            canEdit={canEdit}
            startBatch={startBatch}
            rerunJob={rerunJob}
            showPrePriced={showPrePriced}
            applyLocalOverride={applyLocalOverride}
            onItemsRefresh={onChange}
          />
        </CardBody>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Live summary stats
// ---------------------------------------------------------------------------

function PricingStatsPanel({
  items,
  live,
  batchStatus,
  batchRunning,
}: {
  items: JobItem[];
  live: Record<string, LiveJobSummary>;
  batchStatus: LiveBatchStatus | null;
  batchRunning: boolean;
}) {
  const stats = useMemo(() => {
    const total = items.length;
    let priced = 0;
    let totalValue = 0;
    let inFlight = 0;
    let needsReview = 0;
    let confident = 0;
    let errored = 0;
    let overridden = 0;

    for (const it of items) {
      const snap = buildSnapshot(it, live[it.id]);
      if (snap.inFlight) inFlight++;

      const value = effectivePrice(it);
      if (value != null) {
        priced++;
        const qty = it.qty && it.qty > 0 ? it.qty : 1;
        totalValue += value * qty;
      }
      if (it.priceOverridden) overridden++;

      if (snap.stage && TERMINAL_STAGES.has(snap.stage)) {
        if (snap.error) errored++;
        else if (snap.needsReview) needsReview++;
        else if (snap.confidence != null) confident++;
      }
    }
    return { total, priced, totalValue, inFlight, needsReview, confident, errored, overridden };
  }, [items, live]);

  const elapsed = batchStatus?.elapsed_s != null ? Math.round(batchStatus.elapsed_s) : null;

  return (
    <div className="rounded-md border border-border-subtle bg-bg-surface-2 p-4 space-y-3">
      {batchRunning && batchStatus && (
        <div>
          <div className="flex items-center justify-between mb-1.5 text-sm">
            <div className="flex items-center gap-2 text-text-secondary">
              <Spinner size="sm" />
              <span>
                Pricing… {batchStatus.completed} of {batchStatus.total}
                {stats.inFlight > 0 && (
                  <span className="text-text-tertiary ml-1.5">· {stats.inFlight} in flight</span>
                )}
              </span>
            </div>
            <span className="text-accent-base tabular-nums font-semibold">
              {Math.round(batchStatus.progress)}%
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-surface-3">
            <div
              className="h-full bg-accent-base transition-[width] duration-300"
              style={{ width: `${Math.round(batchStatus.progress)}%` }}
            />
          </div>
          {elapsed != null && (
            <div className="mt-1.5 text-xs text-text-tertiary tabular-nums">
              elapsed {fmtDuration(elapsed)}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat
          label="Items priced"
          value={`${stats.priced} / ${stats.total}`}
          sub={stats.overridden > 0 ? `${stats.overridden} overridden` : undefined}
        />
        <Stat label="Total value" value={fmtCAD(stats.totalValue)} />
        <Stat
          label="Needs review"
          value={String(stats.needsReview + stats.errored)}
          variant={stats.needsReview + stats.errored > 0 ? 'warning' : 'neutral'}
          sub={stats.errored > 0 ? `${stats.errored} errored` : undefined}
        />
        <Stat
          label="Auto-accepted"
          value={String(stats.confident)}
          variant={stats.confident > 0 ? 'success' : 'neutral'}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review table
// ---------------------------------------------------------------------------

/** "Pre-priced" = greg_value set, no engine run yet. */
function isPrePriced(it: JobItem, l: LiveJobSummary | undefined): boolean {
  if (l) return false;
  return it.greg_value != null && it.greg_value !== 0 && !it.pricingBatchId;
}

type SortKey = 'item' | 'price' | 'confidence' | 'tier' | 'status';
type SortDir = 'asc' | 'desc';

// Default order: the order Greg dictated the items (their recorded sortOrder).
// He explicitly asked the list NOT to reshuffle needs-review / low-confidence
// rows to the top after a pricing run — that loses his place mid-review. Sorting
// by any column is still available on demand (applyUserSort); this is only the
// default when no column sort is active. (May 28 session asks #1 + #2.)
function sortByDictationOrder(snaps: RowSnapshot[]): RowSnapshot[] {
  return [...snaps].sort((a, b) => a.item.sortOrder - b.item.sortOrder);
}

function applyUserSort(snaps: RowSnapshot[], key: SortKey, dir: SortDir): RowSnapshot[] {
  const factor = dir === 'asc' ? 1 : -1;
  const nullsLast = (v: number | null): number => (v == null ? Number.POSITIVE_INFINITY : v);
  return [...snaps].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'item':
        cmp = (a.item.description ?? '').localeCompare(b.item.description ?? '');
        break;
      case 'price':
        cmp = nullsLast(a.effective) - nullsLast(b.effective);
        break;
      case 'confidence': {
        const ar = a.confidence ? CONFIDENCE_RANK[a.confidence] ?? -1 : -1;
        const br = b.confidence ? CONFIDENCE_RANK[b.confidence] ?? -1 : -1;
        cmp = ar - br;
        break;
      }
      case 'tier': {
        cmp = (a.pipelinePathLabel ?? '~').localeCompare(b.pipelinePathLabel ?? '~');
        break;
      }
      case 'status': {
        const ai = a.inFlight ? 0 : 1;
        const bi = b.inFlight ? 0 : 1;
        cmp = ai - bi;
        break;
      }
    }
    if (cmp !== 0) return cmp * factor;
    return a.item.sortOrder - b.item.sortOrder;
  });
}

function SortHeader({
  active,
  dir,
  align,
  onClick,
  children,
  className,
}: {
  active: boolean;
  dir: SortDir;
  align?: 'right' | 'center';
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const cellAlign = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const btnExtra = align === 'right' ? 'flex-row-reverse' : '';
  return (
    <th className={`px-3 py-2 text-xs font-semibold text-text-secondary uppercase ${cellAlign} ${className ?? ''}`}>
      <button
        type="button"
        onClick={onClick}
        className={
          'inline-flex items-center gap-1 hover:text-text-primary ' +
          (active ? 'text-text-primary' : 'text-text-secondary') +
          ' ' + btnExtra
        }
      >
        <Icon className="size-3" aria-hidden />
        <span>{children}</span>
      </button>
    </th>
  );
}

interface PriceReviewTableProps {
  jobId: string;
  items: JobItem[];
  live: Record<string, LiveJobSummary>;
  canEdit: boolean;
  startBatch: (itemIds: string[], label: string) => Promise<void>;
  rerunJob: (engineJobId: string) => Promise<void>;
  showPrePriced: boolean;
  applyLocalOverride: (itemId: string, acceptedPrice: number | null, priceOverridden: boolean) => void;
  onItemsRefresh: () => void | Promise<void>;
}

function PriceReviewTable({
  jobId,
  items,
  live,
  canEdit,
  startBatch,
  rerunJob,
  showPrePriced,
  applyLocalOverride,
  onItemsRefresh,
}: PriceReviewTableProps) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);

  const toggleSort = (key: SortKey) => {
    setSort((cur) => {
      if (cur?.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  // Accordion: keep only one row's evidence panel open at a time. Greg asked
  // that opening a new row collapse the previous one instead of stacking up a
  // dozen panels he has to scroll past. (May 28 session ask #6.)
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const snaps = useMemo(() => {
    const filtered = showPrePriced
      ? items
      : items.filter((it) => !isPrePriced(it, live[it.id]));
    return filtered.map((it) => buildSnapshot(it, live[it.id]));
  }, [items, live, showPrePriced]);

  // Frozen row order. Greg works top-down through a value-sorted list, editing
  // prices as he goes — if the list re-sorted on every edit he'd lose his place
  // (his exact complaint). So the order is recomputed only on an EXPLICIT
  // trigger: a column-sort click, or a change in which rows are present. A
  // value edit (price / confidence) never reorders the list. (May 28 ask #3.)
  const membershipKey = useMemo(
    () => snaps.map((s) => s.item.id).sort().join(','),
    [snaps],
  );
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  useEffect(() => {
    const ordered = sort
      ? applyUserSort(snaps, sort.key, sort.dir)
      : sortByDictationOrder(snaps);
    setOrderedIds(ordered.map((s) => s.item.id));
    // `snaps` is read for current values at sort time but deliberately excluded
    // from deps — depending on it would re-sort on every value edit, which is
    // exactly the bug we're fixing. Re-sort only on sort change / membership change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipKey, sort]);

  const sorted = useMemo(() => {
    const byId = new Map(snaps.map((s) => [s.item.id, s]));
    const placed = orderedIds
      .map((id) => byId.get(id))
      .filter((s): s is RowSnapshot => s != null);
    // Defensive: append any rows not yet placed by the order effect (e.g. a
    // brand-new item rendered before the effect runs), in dictation order, so
    // nothing silently disappears from the table.
    const placedIds = new Set(orderedIds);
    const unplaced = snaps
      .filter((s) => !placedIds.has(s.item.id))
      .sort((a, b) => a.item.sortOrder - b.item.sortOrder);
    return [...placed, ...unplaced];
  }, [snaps, orderedIds]);

  const headerProps = (key: SortKey) => ({
    active: sort?.key === key,
    dir: (sort?.key === key ? sort.dir : 'asc') as SortDir,
    onClick: () => toggleSort(key),
  });

  const priceInputs = useRef<Map<string, HTMLInputElement>>(new Map());
  const sortedIds = useMemo(() => sorted.map((s) => s.item.id), [sorted]);

  const focusPriceAdjacent = useCallback(
    (currentId: string, direction: 1 | -1) => {
      const idx = sortedIds.indexOf(currentId);
      if (idx < 0) return;
      for (let i = idx + direction; i >= 0 && i < sortedIds.length; i += direction) {
        const el = priceInputs.current.get(sortedIds[i]!);
        if (el && !el.disabled) {
          el.focus();
          el.select();
          return;
        }
      }
    },
    [sortedIds],
  );

  const registerPriceInput = useCallback((id: string, el: HTMLInputElement | null) => {
    if (el) priceInputs.current.set(id, el);
    else priceInputs.current.delete(id);
  }, []);

  return (
    <div className="overflow-hidden rounded-md border border-border-subtle">
      <table className="w-full border-collapse table-fixed">
        <thead className="bg-bg-surface-2 border-b border-border-subtle">
          <tr>
            <SortHeader {...headerProps('status')} className="w-8">{''}</SortHeader>
            <SortHeader {...headerProps('item')}>Item</SortHeader>
            <SortHeader {...headerProps('price')} align="right" className="w-52">GC value</SortHeader>
            <SortHeader {...headerProps('confidence')} align="center" className="w-32">Confidence</SortHeader>
            <SortHeader {...headerProps('tier')} align="center" className="w-36">Match type</SortHeader>
            <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary uppercase w-40"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((snap) => (
            <PriceReviewRow
              key={snap.item.id}
              jobId={jobId}
              snap={snap}
              canEdit={canEdit}
              startBatch={startBatch}
              rerunJob={rerunJob}
              applyLocalOverride={applyLocalOverride}
              registerPriceInput={registerPriceInput}
              focusPriceAdjacent={focusPriceAdjacent}
              onItemsRefresh={onItemsRefresh}
              expanded={expandedId === snap.item.id}
              onToggleExpand={() =>
                setExpandedId((cur) => (cur === snap.item.id ? null : snap.item.id))
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Inline description editor for the pricing row. Greg kept hitting dictated
// names he wanted to fix (e.g. "double-ped desk", "QBD CD26HC" spacing) but had
// to leave for the Dictation card to do it — which used to wipe his overrides
// (the P0). Editing here PATCHes /dictation and refreshes items in place, so he
// never leaves the pricing screen. (May 28 session ask #5.)
function ItemDescriptionEditor({
  jobId,
  item,
  canEdit,
  onSaved,
}: {
  jobId: string;
  item: JobItem;
  canEdit: boolean;
  onSaved: () => void | Promise<void>;
}) {
  const { toast } = useToast();
  const [draft, setDraft] = useState(item.description ?? '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Resync the draft when server truth changes and we're not mid-edit.
  useEffect(() => {
    if (!editing) setDraft(item.description ?? '');
  }, [item.description, editing]);

  if (!canEdit) {
    return <span className="flex-1 min-w-0 text-text-primary text-sm">{item.description}</span>;
  }

  const commit = async () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed === '' || trimmed === (item.description ?? '')) {
      setDraft(item.description ?? ''); // empty or unchanged — revert, no write
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/jobs/${jobId}/items/${item.id}/dictation`, { description: trimmed });
      track.interaction('item_description_edited_via_pricing', {
        jobId,
        targetType: 'item',
        targetId: item.id,
      });
      await onSaved();
    } catch (err) {
      toast({
        title: 'Rename failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
      setDraft(item.description ?? '');
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="text"
      value={draft}
      disabled={saving}
      title="Edit the item description — saves the corrected name without re-pricing"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onFocus={() => setEditing(true)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(item.description ?? '');
          setEditing(false);
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      className={
        'flex-1 min-w-0 bg-bg-base rounded border border-border-subtle px-1.5 py-1 text-sm ' +
        'text-text-primary hover:border-border-strong focus:border-accent-base focus:bg-bg-surface-1 ' +
        'focus:outline-none ' +
        (saving ? 'opacity-50' : '')
      }
    />
  );
}

function PriceReviewRow({
  jobId,
  snap,
  canEdit,
  startBatch,
  rerunJob,
  applyLocalOverride,
  registerPriceInput,
  focusPriceAdjacent,
  onItemsRefresh,
  expanded,
  onToggleExpand,
}: {
  jobId: string;
  snap: RowSnapshot;
  canEdit: boolean;
  startBatch: (itemIds: string[], label: string) => Promise<void>;
  rerunJob: (engineJobId: string) => Promise<void>;
  applyLocalOverride: (itemId: string, acceptedPrice: number | null, priceOverridden: boolean) => void;
  registerPriceInput: (id: string, el: HTMLInputElement | null) => void;
  focusPriceAdjacent: (currentId: string, direction: 1 | -1) => void;
  onItemsRefresh: () => void | Promise<void>;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const { item, stage, inFlight, effective } = snap;
  const [override, setOverride] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isFocused && !busy) {
      setOverride(effective != null ? String(effective) : '');
    }
  }, [effective, isFocused, busy]);

  const lastSavedRef = useRef<string>('');
  useEffect(() => {
    if (effective != null) lastSavedRef.current = String(effective);
  }, [effective]);

  const setPrice = useCallback(
    async (price: number | null) => {
      setBusy(true);
      try {
        const res = await api.patch<{ ok: boolean; overridden: boolean }>(
          `/api/jobs/${jobId}/items/${item.id}`,
          { acceptedPrice: price },
        );
        applyLocalOverride(item.id, price, !!res.overridden);
        track.interaction(price == null ? 'price_cleared_via_ui' : 'price_accepted_via_ui', {
          jobId,
          targetType: 'item',
          targetId: item.id,
          extra: {
            acceptedPrice: price,
            suggestedGc: item.suggestedGc ?? null,
            overridden: !!res.overridden,
          },
        });
      } catch (err) {
        toast({
          title: 'Save failed',
          description: err instanceof Error ? err.message : String(err),
          variant: 'danger',
        });
      } finally {
        setBusy(false);
      }
    },
    [jobId, item.id, item.suggestedGc, applyLocalOverride, toast],
  );

  const commitOverride = () => {
    const trimmed = override.trim();
    if (trimmed === '' || trimmed === lastSavedRef.current) return;
    const raw = Number(trimmed);
    if (!Number.isFinite(raw) || raw < 0) {
      toast({ title: 'Enter a valid non-negative number', variant: 'danger' });
      setOverride(lastSavedRef.current);
      return;
    }
    const n = roundToNearest5(raw);
    lastSavedRef.current = String(n);
    if (String(n) !== trimmed) setOverride(String(n));
    void setPrice(n);
  };
  const onClearOverride = () => {
    lastSavedRef.current = '';
    setOverride('');
    void setPrice(null);
  };

  // One-click "use my dictated value": commit Greg's value as the price, collapse
  // the row's evidence, and jump focus to the next item. Greg rips through the
  // cheap stuff this way ("click the Greg price to put it back in"). (May 28 #7.)
  const acceptGregValue = useCallback(async () => {
    if (item.greg_value == null || item.greg_value <= 0) return;
    await setPrice(item.greg_value);
    if (expanded) onToggleExpand();
    focusPriceAdjacent(item.id, 1);
  }, [item.greg_value, item.id, setPrice, expanded, onToggleExpand, focusPriceAdjacent]);

  const onPriceThis = () => void startBatch([item.id], item.description ?? 'item');

  const onStopThis = async () => {
    if (!snap.engineJobId) return;
    setBusy(true);
    try {
      await api.post(`/api/jobs/${jobId}/pricing-jobs/${snap.engineJobId}/cancel`, {});
    } catch (err) {
      toast({
        title: 'Stop failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  const displayValue = override.trim() === '' ? '' : fmtCAD(Number(override));
  const isOverridden = !!item.priceOverridden;

  const canExpand = !!snap.decision || inFlight || effective != null || !!snap.error;
  const onRowClick = () => {
    if (canExpand) onToggleExpand();
  };
  const stopRowClick = (e: { stopPropagation: () => void }) => e.stopPropagation();

  const confVariant = confidenceVariant(snap);

  return (
    <>
      <tr
        className={
          'border-b border-border-subtle last:border-0 align-top hover:bg-bg-surface-2 ' +
          (canExpand ? 'cursor-pointer' : '')
        }
        onClick={onRowClick}
      >
        <td className="px-3 py-3">
          <button
            type="button"
            className="text-text-secondary hover:text-text-primary disabled:opacity-40"
            aria-label={expanded ? 'Collapse evidence' : 'Expand evidence'}
            onClick={(e) => {
              stopRowClick(e);
              if (canExpand) onToggleExpand();
            }}
            disabled={!canExpand}
          >
            {expanded ? (
              <ChevronDown className="size-4" aria-hidden />
            ) : (
              <ChevronRightIcon className="size-4" aria-hidden />
            )}
          </button>
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-1.5">
            {item.qty != null && item.qty > 1 && (
              <span className="text-text-tertiary tabular-nums shrink-0 text-sm">{item.qty} ×</span>
            )}
            <ItemDescriptionEditor
              jobId={jobId}
              item={item}
              canEdit={canEdit}
              onSaved={onItemsRefresh}
            />
          </div>
          {(item.isLeasehold || inFlight || snap.degraded || snap.isWalkIn) && (
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {item.isLeasehold && <Badge variant="warning">Leasehold</Badge>}
              {snap.isWalkIn && <Badge variant="info">Walk-in</Badge>}
              {snap.degraded && !inFlight && (
                <span className="inline-flex items-center gap-1 text-xs text-warning">
                  <AlertTriangle className="size-3" aria-hidden />
                  degraded sources
                </span>
              )}
              {inFlight && (
                <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
                  <Spinner size="sm" />
                  <span>{snap.stageLabel ?? stage?.replace(/_/g, ' ')}</span>
                </span>
              )}
            </div>
          )}
        </td>
        <td className="px-3 py-3 text-right text-sm tabular-nums">
          <div className="flex items-center justify-end gap-1">
            {snap.error && !inFlight && (
              <span
                title={`${snap.error.kind}: ${snap.error.message}`}
                className="inline-flex text-danger"
                aria-label="engine error"
              >
                <AlertCircle className="size-3.5" aria-hidden />
              </span>
            )}
            {isOverridden && canEdit && (
              <button
                type="button"
                title="Clear override — revert to the engine suggestion or Greg's value"
                className="text-warning hover:text-text-primary disabled:opacity-40"
                disabled={busy}
                onClick={(e) => {
                  stopRowClick(e);
                  onClearOverride();
                }}
              >
                <Undo2 className="size-3.5" aria-label="clear override" />
              </button>
            )}
            {canEdit && !inFlight ? (
              <input
                type="text"
                inputMode="decimal"
                title="Going-concern value. Type a GC number; the xlsx export uses this value as-is."
                ref={(el) => registerPriceInput(item.id, el)}
                value={isFocused ? override : displayValue}
                disabled={busy}
                placeholder="—"
                onClick={stopRowClick}
                onChange={(e) => setOverride(e.currentTarget.value)}
                onFocus={(e) => {
                  setIsFocused(true);
                  track.interaction('price_field_focused', {
                    jobId,
                    targetType: 'item',
                    targetId: item.id,
                  });
                  const el = e.currentTarget;
                  requestAnimationFrame(() => el.select());
                }}
                onBlur={() => {
                  commitOverride();
                  setIsFocused(false);
                  track.interaction('price_field_blurred', {
                    jobId,
                    targetType: 'item',
                    targetId: item.id,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitOverride();
                    (e.currentTarget as HTMLInputElement).blur();
                  } else if (e.key === 'Escape') {
                    setOverride(lastSavedRef.current);
                    (e.currentTarget as HTMLInputElement).blur();
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    commitOverride();
                    focusPriceAdjacent(item.id, 1);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    commitOverride();
                    focusPriceAdjacent(item.id, -1);
                  }
                }}
                className={
                  'w-40 bg-bg-base px-2 py-1.5 rounded border border-border-subtle text-right text-base ' +
                  'hover:border-border-strong focus:border-accent-base focus:bg-bg-surface-1 ' +
                  'focus:outline-none font-semibold text-text-primary tabular-nums ' +
                  (busy ? 'opacity-50' : '')
                }
              />
            ) : effective != null ? (
              <span className="font-semibold text-text-primary text-base px-2">{fmtCAD(effective)}</span>
            ) : inFlight ? (
              <span className="text-text-tertiary px-2">…</span>
            ) : (
              <span className="text-text-tertiary px-2">—</span>
            )}
          </div>
        </td>
        <td className="px-3 py-3 text-center">
          {/* In-flight rows show progress in the Item column — keep this cell
              blank until the engine settles a confidence. */}
          {inFlight ? null : snap.confidenceLabel ? (
            <Badge variant={confVariant}>{snap.confidenceLabel}</Badge>
          ) : (
            <span className="text-xs text-text-tertiary">—</span>
          )}
        </td>
        <td className="px-3 py-3 text-center">
          {snap.pipelinePathLabel ? (
            <Badge variant="neutral">{snap.pipelinePathLabel}</Badge>
          ) : inFlight ? null : snap.isWalkIn ? (
            <Badge variant="neutral">Manual — walk-in</Badge>
          ) : (
            <span className="text-xs text-text-tertiary">—</span>
          )}
        </td>
        <td className="px-3 py-3">
          {canEdit && inFlight && snap.engineJobId && (
            <div className="flex flex-col gap-1.5">
              <Button
                variant="danger"
                size="sm"
                fullWidth
                loading={busy}
                leftIcon={<Square className="size-3.5" aria-hidden />}
                onClick={(e) => {
                  stopRowClick(e);
                  void onStopThis();
                }}
                title="Stop pricing this item — other items keep running"
              >
                Stop
              </Button>
            </div>
          )}
          {canEdit && !inFlight && (
            // Not gated on a running batch — re-pricing one row starts its own
            // batch, which the poll loop tracks alongside any others in flight.
            <div className="flex flex-col gap-1.5">
              {item.greg_value != null &&
                item.greg_value > 0 &&
                item.acceptedPrice !== item.greg_value && (
                  <Button
                    variant="primary"
                    size="sm"
                    fullWidth
                    loading={busy}
                    onClick={(e) => {
                      stopRowClick(e);
                      void acceptGregValue();
                    }}
                    title={`Accept your dictated value (${fmtCAD(item.greg_value)}) and move to the next item`}
                  >
                    Use {fmtCAD(item.greg_value)}
                  </Button>
                )}
              <Button
                variant={effective == null ? 'primary' : 'secondary'}
                size="sm"
                fullWidth
                loading={busy}
                onClick={(e) => {
                  stopRowClick(e);
                  onPriceThis();
                }}
                title={effective == null ? 'Price this item' : 'Re-price this item'}
              >
                {effective == null ? 'Price' : 'Re-price'}
              </Button>
            </div>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border-subtle last:border-0">
          <td></td>
          <td colSpan={5} className="px-3 py-3 bg-bg-surface-2/50 overflow-hidden">
            <PricingEvidence snap={snap} rerunJob={rerunJob} canEdit={canEdit} />
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Evidence panel — v2-native
// ---------------------------------------------------------------------------

function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'danger' | 'warning' | 'neutral';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'danger'
      ? 'border-danger/30 bg-danger/10'
      : tone === 'warning'
        ? 'border-warning/30 bg-warning-muted'
        : 'border-border-subtle bg-bg-surface-1';
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${cls}`}>
      <div className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5">{icon}</span>
        <div className="space-y-1">{children}</div>
      </div>
    </div>
  );
}

function CandidateLink({ c }: { c: PricingCandidate }) {
  const price = c.price_cad != null ? fmtCAD(c.price_cad) : 'price n/a';
  return (
    <li className="flex items-start gap-2 text-xs">
      <span className="text-text-secondary tabular-nums shrink-0 w-20 text-right">{price}</span>
      {c.url ? (
        <a
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent-base hover:text-accent-hover inline-flex items-center gap-1 truncate"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="size-3 shrink-0" aria-hidden />
          <span className="truncate">{c.product_name}</span>
        </a>
      ) : (
        <span className="text-text-secondary truncate">{c.product_name}</span>
      )}
      <span className="text-text-tertiary shrink-0">· {c.supplier}</span>
    </li>
  );
}

function CandidateGroup({
  title,
  tone,
  candidates,
  defaultOpen,
}: {
  title: string;
  tone: 'success' | 'info' | 'neutral';
  candidates: PricingCandidate[];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (candidates.length === 0) return null;
  const dot =
    tone === 'success' ? 'bg-success' : tone === 'info' ? 'bg-accent-base' : 'bg-text-tertiary';
  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-text-primary"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {open ? <ChevronDown className="size-3" aria-hidden /> : <ChevronRightIcon className="size-3" aria-hidden />}
        <span className={`inline-block size-1.5 rounded-full ${dot}`} aria-hidden />
        {title} ({candidates.length})
      </button>
      {open && (
        <ul className="mt-1 ml-4 space-y-0.5">
          {candidates.map((c, i) => (
            <CandidateLink key={c.url ?? `${c.supplier}-${i}`} c={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function PricingEvidence({
  snap,
  rerunJob,
  canEdit,
}: {
  snap: RowSnapshot;
  rerunJob: (engineJobId: string) => Promise<void>;
  canEdit: boolean;
}) {
  const { toast } = useToast();
  const [rerunning, setRerunning] = useState(false);
  const decision = snap.decision;

  // In-flight, no decision yet — show live stage progress.
  if (!decision && snap.inFlight) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Spinner size="sm" />
        <span className="font-medium text-text-primary">
          {snap.stageLabel ?? snap.stage?.replace(/_/g, ' ') ?? 'Working…'}
        </span>
      </div>
    );
  }

  const onRerun = async () => {
    if (!snap.engineJobId) {
      toast({ title: 'Cannot re-run — engine job id unknown', variant: 'danger' });
      return;
    }
    setRerunning(true);
    try {
      await rerunJob(snap.engineJobId);
      toast({ title: 'Item re-priced', variant: 'success' });
    } catch (err) {
      toast({
        title: 'Re-run failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setRerunning(false);
    }
  };

  // Banners (rendered for both the decision and decision-less cases).
  const errorBanner = snap.error ? (
    <Banner tone="danger" icon={<AlertCircle className="size-4 text-danger" aria-hidden />}>
      <div className="font-semibold text-danger capitalize">
        Engine error — {snap.error.kind.replace(/_/g, ' ')}
      </div>
      {snap.error.message && (
        <div className="text-xs text-text-secondary font-mono break-all">{snap.error.message}</div>
      )}
    </Banner>
  ) : null;

  const health = decision?.retrieval_health;
  const degradedBanner = health?.degraded ? (
    <Banner tone="warning" icon={<AlertTriangle className="size-4 text-warning" aria-hidden />}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-text-primary">
            Priced from {health.sources_ok} of {health.sources_attempted} sources
          </div>
          <div className="text-xs text-text-secondary">
            {(health.sources_failed ?? []).length} supplier
            {(health.sources_failed ?? []).length === 1 ? '' : 's'} temporarily unavailable:{' '}
            {(health.sources_failed ?? [])
              .map((f) => `${f.supplier} (${f.reason})`)
              .join(', ')}
            . The candidate pool may be incomplete.
          </div>
        </div>
        {canEdit && (
          <Button
            variant="secondary"
            size="sm"
            loading={rerunning}
            leftIcon={<Wand2 className="size-3.5" aria-hidden />}
            onClick={(e) => {
              e.stopPropagation();
              void onRerun();
            }}
          >
            Re-run
          </Button>
        )}
      </div>
    </Banner>
  ) : null;

  const reviewBanner =
    snap.needsReview && snap.reviewReason ? (
      <Banner tone="warning" icon={<AlertTriangle className="size-4 text-warning" aria-hidden />}>
        <div className="font-medium text-text-primary">Needs review</div>
        <div className="text-xs text-text-secondary">{snap.reviewReason}</div>
      </Banner>
    ) : null;

  if (!decision) {
    return (
      <div className="space-y-3">
        {errorBanner}
        {reviewBanner}
        {!errorBanner && !reviewBanner && (
          <div className="text-sm text-text-tertiary">
            No engine evidence captured for this item yet — click “Price” to run pricing on it.
          </div>
        )}
      </div>
    );
  }

  // --- Corrections callout ---
  const corrections = decision.corrections_applied ?? [];
  const correctionsCallout =
    corrections.length > 0 ? (
      <Banner tone="neutral" icon={<Wand2 className="size-4 text-accent-base" aria-hidden />}>
        <div className="font-medium text-text-primary">
          Likely dictation {corrections.length === 1 ? 'correction' : 'corrections'}
        </div>
        <ul className="text-xs text-text-secondary space-y-0.5">
          {corrections.map((c, i) => (
            <li key={i}>
              <span className="uppercase tracking-wide text-text-tertiary">{c.field}</span>{' '}
              <span className="line-through">{c.from}</span> → <strong>{c.to}</strong>
              {c.reason ? <span className="text-text-tertiary"> — {c.reason}</span> : null}
            </li>
          ))}
        </ul>
      </Banner>
    ) : null;

  // --- Valuation ladder + greg_value_check ---
  const v = decision.valuation;
  const gvc = decision.greg_value_check;
  const hasLadder = v && (v.rv != null || v.gc != null);
  const verdict = gvc?.verdict;
  const gvcBanner =
    verdict === 'suspect_low' || verdict === 'suspect_high' ? (
      <Banner tone="warning" icon={<AlertTriangle className="size-4 text-warning" aria-hidden />}>
        <div className="font-medium text-text-primary">
          Engine price looks {verdict === 'suspect_low' ? 'low' : 'high'} vs Greg’s value
        </div>
        <div className="text-xs text-text-secondary">
          {verdict === 'suspect_low'
            ? 'The engine price is far below Greg’s dictated value — it may have matched a part or a used listing.'
            : 'The engine price is far above Greg’s dictated value — it may have matched a larger or pricier product.'}
          {gvc?.ratio != null && <> (engine GC / Greg ≈ {gvc.ratio.toFixed(2)}×)</>}
        </div>
      </Banner>
    ) : null;

  // --- Candidate pool, grouped by the rerank's classification ---
  const pool = decision.candidate_pool ?? [];
  const exact = pool.filter((c) => c.classification === 'exact');
  const similar = pool.filter((c) => c.classification === 'similar');
  const other = pool.filter((c) => c.classification !== 'exact' && c.classification !== 'similar');

  const chosen = decision.chosen_source;

  return (
    <div className="space-y-3 text-sm">
      {errorBanner}
      {reviewBanner}
      {degradedBanner}
      {gvcBanner}
      {correctionsCallout}

      {hasLadder && (
        <div className="rounded-md border border-border-subtle bg-bg-surface-1 px-3 py-2 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-xs">
          <ValuationCell label="RV" hint="Replacement Value (new retail)" value={v?.rv} />
          <ValuationCell label="GC" hint="Going Concern" value={v?.gc} emphasize />
          <ValuationCell label="OLV" hint="Orderly Liquidation Value" value={v?.olv} />
          <ValuationCell label="FLV" hint="Forced Liquidation Value" value={v?.flv} />
          {decision.greg_value != null && decision.greg_value > 0 && (
            <ValuationCell
              label="Greg"
              hint="Greg's dictated value"
              value={decision.greg_value}
            />
          )}
          {decision.depreciation && (
            <span className="text-text-tertiary" title="RV → GC depreciation basis">
              <span className="uppercase tracking-wide">basis:</span> {decision.depreciation.basis}
            </span>
          )}
        </div>
      )}

      {decision.match_reason && (
        <div className="text-text-secondary">
          <span className="text-text-tertiary">Match: </span>
          {decision.match_reason}
        </div>
      )}

      {chosen && (
        <div className="rounded-md border border-success/30 bg-success-muted px-3 py-2">
          <div className="text-xs uppercase tracking-wide text-text-tertiary mb-0.5">Chosen source</div>
          <div className="flex items-center gap-2">
            <span className="text-text-primary tabular-nums font-semibold">
              {fmtCAD(chosen.price_cad)}
            </span>
            {chosen.url ? (
              <a
                href={chosen.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-base hover:text-accent-hover inline-flex items-center gap-1 text-xs truncate"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{chosen.product_name}</span>
              </a>
            ) : (
              <span className="text-text-secondary text-xs truncate">{chosen.product_name}</span>
            )}
            <span className="text-text-tertiary text-xs">· {chosen.supplier}</span>
          </div>
        </div>
      )}

      {pool.length > 0 ? (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-text-tertiary">
            Candidate pool ({pool.length})
          </div>
          <CandidateGroup title="Exact" tone="success" candidates={exact} defaultOpen />
          <CandidateGroup title="Similar" tone="info" candidates={similar} defaultOpen />
          <CandidateGroup title="Other" tone="neutral" candidates={other} defaultOpen={false} />
        </div>
      ) : (
        <div className="text-text-tertiary">No candidates were retrieved for this item.</div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-tertiary pt-1 border-t border-border-subtle">
        {decision.pipeline_path_label && <span>{decision.pipeline_path_label}</span>}
        {decision.cost?.usd != null && decision.cost.usd > 0 && (
          <span>cost ≈ ${decision.cost.usd.toFixed(4)}</span>
        )}
        {decision.elapsed_s != null && <span>{fmtDuration(decision.elapsed_s)}</span>}
      </div>
    </div>
  );
}
